import CategoryPage from 'components/pages/Category';

import getServerDateProps from 'lib/date/getServerDateProps';
import { toAbsoluteLink, toCategoryLink } from 'lib/links';
import { resolvify } from 'lib/promises';

import { getAds } from 'services/ads';
import {
  getCategoriesList,
  getCategoryInfo,
  getCategoryOffersList,
} from 'services/category';
import { getLandingsList } from 'services/landing';
import { getStoresList } from 'services/store';

import APP from 'constants/app';
import CATEGORY from 'constants/category';
import LINKS from 'constants/links';

const Category = (props) => {
  /**
   * During Server-Side Rendering (SSR) when Next.js
   * tries to render the component for error generation/stack traces,
   * it sometimes calls the component with undefined props. The
   * `catch` block in `getServerSideProps` redirects on error, but
   * there's  probably a race condition where Sentry's
   * `wrapPageComponentWithSentry` tries to render the component before
   * the redirect happens, passing `undefined as props. We just return
   * `null` to avoid the error when this happens.
   */
  if (!props?.category) {
    return null;
  }

  const { canonicalUrl, category, serverDate, serverOffers } = props;

  return (
    <CategoryPage
      canonicalUrl={canonicalUrl}
      category={category}
      serverDate={serverDate}
      serverOffers={serverOffers}
    />
  );
};

export const getServerSideProps = async ({
  query,
  params: { categorySlug },
}) => {
  try {
    const [
      category,
      serverOffers,
      categories,
      stores,
      featuredLandings,
      highlightedLandings,
      {
        data: {
          topAd: [topbarAd = null],
          topbarHighlight: navbarAds,
        },
      },
    ] = await resolvify([
      getCategoryInfo({
        categorySlug: categorySlug,
      }),
      getCategoryOffersList({
        categorySlug: categorySlug,
        limit: CATEGORY.OFFERS.SERVER_LIMIT,
        ...query,
      }),
      {
        fn: getCategoriesList({ selectedCategories: true }),
        fallback: getCategoriesList.fallback,
      },
      {
        fn: getStoresList({ from: 'topbar' }),
        fallback: getStoresList.fallback,
      },
      {
        fn: getLandingsList({ featured: true }),
        fallback: getLandingsList.fallback,
      },
      {
        fn: getLandingsList({ highlighted: true }),
        fallback: getLandingsList.fallback,
      },
      {
        fn: getAds(),
        fallback: getAds.fallback,
      },
    ]);

    return {
      props: {
        canonicalUrl: toAbsoluteLink(toCategoryLink(categorySlug)),
        category,
        serverOffers,
        ...getServerDateProps(),
        [APP.CTX_OPTIONAL_PROPS]: {
          categories,
          featuredLandings,
          highlightedLandings,
          navbarAds,
          stores,
          topbarAd,
        },
      },
    };
  } catch {
    return { redirect: { destination: LINKS[404], permanent: false } };
  }
};

export default Category;
